import { WordOption } from "@/components/exercises/grammar/Desktop/MatchingOptionExercise";
import {
  TUserLang,
  ProductItem,
  ExerciseDONE,
  ExerciseTODO,
  CorrectionResult,
  ExerciseOptionTypes,
  ExerciseGroupingsItem,
} from "@/types";
import dayjs from "dayjs";

export const IELTS_SCORE = {
  "39-40": 9,
  "37-38": 8.5,
  "35-36": 8,
  "32-34": 7.5,
  "30-31": 7,
  "26-29": 6.5,
  "23-25": 6,
  "18-22": 5.5,
  "16-17": 5,
  "13-15": 4.5,
  "11-12": 4,
  "0-10": 0,
};

export const READING_SCORE = {
  "39-40": 9,
  "36-38": 8.5,
  "35": 8,
  "31-34": 7.5,
  "30": 7,
  "24-29": 6.5,
  "23": 6,
  "16-22": 5.5,
  "15": 5,
  "13-14": 4.5,
  "10-12": 4,
  "8-9": 3.5,
  "6-7": 3,
  "4-5": 2.5,
};

type Votes = typeof IELTS_SCORE | typeof READING_SCORE;

export const getScore = (score: number, votes: Votes): number => {
  for (const [range, value] of Object.entries(votes)) {
    const rangeValues = range.split("-").map(Number);

    if (score >= rangeValues[0] && score <= rangeValues[1]) {
      return value;
    }
  }
  return 0;
};

export const renderExerciseType = (type: string) => {
  switch (type.toLowerCase()) {
    case "writing":
      return "📝 Writing";
    case "grammar":
      return "👩‍🏫 Grammar";
    case "vocabulary":
      return "🗣️ Vocabulary";
    case "reading":
      return "📗 Reading";
    case "listening":
      return "🔈 Listening";
    case "speaking":
      return "🗣️ Speaking";
    case "comprehension":
      return "👩‍🏫 Comprehension";
    default:
      return "Unknown Type";
  }
};

export const stringToExerciseTypeKey = (
  typeString: string,
): keyof typeof ExerciseOptionTypes | undefined => {
  const mapping: { [key: string]: keyof typeof ExerciseOptionTypes } = {
    true_false: "TrueFalse",
    multiple_choice: "MultipleChoice",
    matching_option: "MatchingOption",
    gap_fill: "GapFill",
    groupings: "Grouping",
  };

  if (!mapping[typeString]) {
    throw new Error("Invalid exercise type string: " + typeString);
  }
  return mapping[typeString];
};

export const renderExerciseTypePot = (type: ExerciseOptionTypes): string => {
  switch (type) {
    case ExerciseOptionTypes.TrueFalse:
      return "True/False";
    case ExerciseOptionTypes.MultipleChoice:
      return "Multiple Choice";
    case ExerciseOptionTypes.MatchingOption:
      return "Matching Option";
    case ExerciseOptionTypes.GapFill:
      return "Gap Fill";
    case ExerciseOptionTypes.Grouping:
      return "Grouping";
    default:
      return "Unknown Type";
  }
};

function filterExercisesByDate<
  T extends {
    id: number;
    is_precourse: number;
    lang: TUserLang;
    english_level: string;
  },
>(
  exercises: T[],
  products: ProductItem[],
  dateField: keyof T,
  lang: TUserLang,
  englishLevel: string,
): T[] {
  const filteredExercises: T[] = [];

  for (const product of products) {
    if (product.pivot?.start_date && product.pivot?.end_date) {
      const startDate = new Date(product.pivot.start_date).toISOString();
      const endDate = dayjs(product.pivot.end_date)
        .add(3, "month")
        .toISOString();

      const filtered = exercises.filter((exercise) => {
        const eventDate = new Date(
          exercise[dateField] as unknown as string,
        ).toISOString();

        if (exercise.is_precourse == 1) {
          return (
            exercise.is_precourse == 1 &&
            exercise.lang == lang &&
            exercise.english_level == englishLevel
          );
        }

        return (
          (eventDate >= startDate && eventDate <= endDate) ||
          exercise.is_precourse == 1
        );
      });

      filteredExercises.push(...filtered);
    }
  }

  return (
    (Array.from(new Set(filteredExercises.map((e) => e.id))).map((id) =>
      filteredExercises.find((e) => e.id === id),
    ) as T[]) || []
  );
}

export function filterExercisesTodoByDate(
  exercises: ExerciseTODO[],
  products: ProductItem[],
  lang: TUserLang,
  englishLevel: string,
): ExerciseTODO[] {
  return filterExercisesByDate(
    exercises,
    products,
    "event_date",
    lang,
    englishLevel,
  );
}

export function filterExercisesDoneByDate(
  exercises: ExerciseDONE[],
  products: ProductItem[],
  lang: TUserLang,
  englishLevel: string,
): ExerciseDONE[] {
  return filterExercisesByDate(exercises, products, "data", lang, englishLevel);
}

// COMPREHENSION EXERCISES

// Add this at the top with other interfaces
export const calculateMaxWidth = (wordOptions: WordOption[]): number => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return 121; // fallback width

  context.font = "14px sans-serif"; // match your font-size and font-family
  const widths = wordOptions.map(
    (option) => context.measureText(option.word).width,
  );
  return Math.max(...widths) + 60; // add padding
};

// Helper function to check correctness
export const getCorrectionResults = (
  category: string,
  words: string[],
  data: ExerciseGroupingsItem[],
): CorrectionResult[] => {
  const correctWordsForCategory = data
    .filter((item) => item.category === category)
    .map((item) => item.word);

  return words.map((word) => ({
    word,
    isCorrect: correctWordsForCategory.includes(word),
  }));
};
